import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  InputAdornment,
  Fade,
  CircularProgress,
  Pagination,
  Stack,
  useMediaQuery
} from '@mui/material';
import {
  Search,
  Clear,
  PlayArrow,
  BookmarkBorder,
  ArrowBack,
  Favorite,
  FavoriteBorder,
  PlayCircleFilled,
  VideoLibrary
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchVideos, filterVideos, getFilterOptions, fetchAllMetadata } from '../api/videos';
import { chakraColors } from '../theme/theme';
import { useTheme } from '@mui/material/styles';
import NavBar from '../components/NavBar';
import Modal from '@mui/material/Modal';
import VideoModal from '../components/VideoModal';
import { useAuth } from '../contexts/AuthContext';

const getInitialFavourites = () => {
  try {
    return JSON.parse(localStorage.getItem('favouriteVideos')) || [];
  } catch {
    return [];
  }
};

// Helper function to parse comma-separated values or return array as-is
const parseCommaSeparated = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }
  return [];
};

// Helper function to capitalize chakra names to match chakraColors keys
const capitalizeChakra = (chakra) => {
  if (!chakra) return '';
  
  // Handle special cases
  const specialCases = {
    'thirdeye': 'ThirdEye',
    'third eye': 'ThirdEye',
    'thirdEye': 'ThirdEye',
    'solarplexus': 'Solar',
    'solar plexus': 'Solar',
    'solarPlexus': 'Solar'
  };

  const lowerChakra = chakra.toLowerCase().replace(/\s+/g, '');
  if (specialCases[lowerChakra]) {
    return specialCases[lowerChakra];
  }
  
  // Capitalize first letter for normal chakras
  return chakra.charAt(0).toUpperCase() + chakra.slice(1).toLowerCase();
};

const VideoCatalog = () => {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [chakraFilter, setChakraFilter] = useState('');
  const [emotionFilter, setEmotionFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState({ chakras: [], emotions: [], categories: [] });
  const [favourites, setFavourites] = useState(getInitialFavourites());
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalVideo, setModalVideo] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(16);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  useEffect(() => {
    // Load all videos on component mount
    const loadVideos = async () => {
      try {
        const [metadata, allVideos] = await Promise.all([
          fetchAllMetadata(),
          fetchVideos()
        ]);
        setVideos(allVideos);
        setFilteredVideos(allVideos);
        
        console.log('Raw metadata:', metadata);
        console.log('metadata.chakras:', metadata.chakras);
        console.log('metadata.emotions:', metadata.emotions);
        console.log('metadata.videoCategories:', metadata.videoCategories);
        
        const fo = {
            chakras: Array.from(metadata.chakras.map(c => c.chakra) || []).sort(),
            emotions: Array.from(metadata.emotions.map(e => e.emotion) || []).sort(),
            categories: Array.from(metadata.videoCategories.map(c => c.category) || []).sort()
          }
        console.log('Processed filterOptions:', fo)
        setFilterOptions(fo);
      } catch (error) {
        console.error('Error loading videos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadVideos();
  }, []);

  useEffect(() => {
    // Apply filters when search term or filters change
    const applyFilters = async () => {
      setLoading(true);
      try {
        if (!searchTerm && !chakraFilter && !emotionFilter && !categoryFilter) {
          setFilteredVideos(videos);
        } else {
          const filtered = await filterVideos(videos, searchTerm, chakraFilter, emotionFilter, categoryFilter);
          setFilteredVideos(filtered);
        }
      } catch (error) {
        console.error('Error filtering videos:', error);
        // On error, show unfiltered videos
        setFilteredVideos(videos);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimeout = setTimeout(applyFilters, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, chakraFilter, emotionFilter, categoryFilter, videos]);

  useEffect(() => {
    localStorage.setItem('favouriteVideos', JSON.stringify(favourites));
  }, [favourites]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClearChakraFilter = () => {
    setChakraFilter('');
  };

  const handleClearEmotionFilter = () => {
    setEmotionFilter('');
  };

  const handleClearCategoryFilter = () => {
    setCategoryFilter('');
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setChakraFilter('');
    setEmotionFilter('');
    setCategoryFilter('');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (chakraFilter) count++;
    if (emotionFilter) count++;
    if (categoryFilter) count++;
    return count;
  };

  const toggleFavourite = (id) => {
    setFavourites((prev) =>
      prev.includes(id) ? prev.filter((fid) => fid !== id) : [...prev, id]
    );
  };

  const openWatchModal = (video) => {
    setModalVideo(video);
    setModalOpen(true);
  };
  
  const closeWatchModal = () => {
    setModalOpen(false);
    setModalVideo(null);
  };

  const filtered = showFavouritesOnly
    ? filteredVideos.filter((v) => favourites.includes(v.id))
    : filteredVideos;

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageVideos = filtered.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, chakraFilter, emotionFilter, categoryFilter, showFavouritesOnly]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const VideoCard = ({ video }) => {
    const isDark = theme.palette.mode === 'dark';
    const chakras = parseCommaSeparated(video.chakras);
    const emotions = parseCommaSeparated(video.emotions);
    const tags = parseCommaSeparated(video.tags);
    // Use the first chakra for determining the glow color
    const primaryChakra = chakras[0] || 'root';
    const capitalizedChakra = capitalizeChakra(primaryChakra);
    const glowColor = chakraColors[capitalizedChakra] || chakraColors['Root'];
    
    return (
      <Fade in={true} timeout={500}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            border: `2px solid ${glowColor}30`,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 8px 24px ${glowColor}60`,
              borderColor: `${glowColor}80`
            }
          }}
        >
          {/* Background Image */}
          {video.backgroundImageUrl && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${video.backgroundImageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: isDark ? 0.15 : 0.1,
                zIndex: 0
              }}
            />
          )}
          
          {/* Gradient Overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: isDark
                ? `linear-gradient(135deg, ${glowColor}22 0%, ${theme.palette.background.paper}CC 100%)`
                : `linear-gradient(135deg, ${glowColor}20 0%, ${theme.palette.background.paper}CC 100%)`,
              zIndex: 1
            }}
          />

          <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                {video.title}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={() => toggleFavourite(video.id)} size="small" color="error">
                  {favourites.includes(video.id) ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
                
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary" >{video.authorName}</Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              paragraph
              sx={{
                lineHeight: 1.6,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minHeight: '4.8em'
              }}
            >
              {video.excerpt}
            </Typography>

            <Box flexGrow={1} />
            <Box display="flex" gap={1} mt={2} flexWrap="wrap">
              {/* Primary Chakra Chip */}
              {primaryChakra && (
                <Chip
                  label={capitalizeChakra(primaryChakra)}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    bgcolor: glowColor,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    cursor: 'default'
                  }}
                />
              )}
              
              {/* Primary Emotion */}
              {emotions[0] && (
                <Chip
                  label={emotions[0]}
                  size="small"
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    borderColor: glowColor,
                    color: glowColor,
                    fontWeight: 500,
                    borderWidth: 2,
                    cursor: 'default'
                  }}
                />
              )}

              {tags && tags.length > 0 && (
                <Chip
                  label={tags[0]}
                  size="small"
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    borderColor: glowColor,
                    color: glowColor,
                    fontWeight: 500,
                    borderWidth: 2,
                    cursor: 'default'
                  }}
                />
              )}
            </Box>
          </CardContent>

          <CardActions sx={{ p: 3, pt: 0, flexShrink: 0, position: 'relative', zIndex: 2 }}>
            <Button
              variant="contained"
              startIcon={<PlayCircleFilled />}
              size="small"
              sx={{ mr: 1, flex: 1 }}
              onClick={() => openWatchModal(video)}
            >
              Watch
            </Button>
          </CardActions>
        </Card>
      </Fade>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        py: 0
      }}
    >
      <NavBar user={user} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box mb={4} display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h4" gutterBottom fontWeight={600}>
              Discover Your Perfect Video
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Explore our curated collection of videos for meditation, healing, and growth
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant={showFavouritesOnly ? 'contained' : 'outlined'}
              color="error"
              startIcon={<Favorite />}
              onClick={() => setShowFavouritesOnly((v) => !v)}
              sx={{ minWidth: 180 }}
            >
              {showFavouritesOnly ? 'Showing Favourites' : 'Show Favourites Only'}
            </Button>
            
          </Box>
        </Box>

        {/* Search and Filters */}
        <Box mb={4}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: '2fr 1fr 1fr 1fr 1fr',
                lg: '2fr 1fr 1fr 1fr 1fr'
              },
              gap: 2,
              alignItems: 'center',
              mb: 2
            }}
          >
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Chakra Filter */}
            <FormControl fullWidth>
              <InputLabel>Chakra</InputLabel>
              <Select
                value={chakraFilter}
                label="Chakra"
                onChange={(e) => setChakraFilter(e.target.value)}
              >
                <MenuItem value="">All Chakras</MenuItem>
                                 {filterOptions.chakras.map((chakra) => (
                   <MenuItem key={chakra} value={chakra}>
                     <Box display="flex" alignItems="center" gap={1}>
                       <Box
                         sx={{
                           width: 12,
                           height: 12,
                           borderRadius: '50%',
                           bgcolor: chakraColors[capitalizeChakra(chakra)] || '#666'
                         }}
                       />
                       {capitalizeChakra(chakra)}
                     </Box>
                   </MenuItem>
                 ))}
              </Select>
            </FormControl>

            {/* Emotion Filter */}
            <FormControl fullWidth>
              <InputLabel>Emotion</InputLabel>
              <Select
                value={emotionFilter}
                label="Emotion"
                onChange={(e) => setEmotionFilter(e.target.value)}
              >
                <MenuItem value="">All Emotions</MenuItem>
                {filterOptions.emotions.map((emotion) => (
                  <MenuItem key={emotion} value={emotion}>
                    {emotion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Category Filter */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {filterOptions.categories?.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>

            {/* Clear Filters */}
            {getActiveFiltersCount() > 0 && (
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClearAllFilters}
                startIcon={<Clear />}
                sx={{ minHeight: 56 }}
              >
                Clear ({getActiveFiltersCount()})
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {getActiveFiltersCount() > 0 && (
            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  onDelete={handleClearSearch}
                  onClick={handleClearSearch}
                  color="primary"
                />
              )}
              {chakraFilter && (
                <Chip
                  label={`Chakra: ${capitalizeChakra(chakraFilter)}`}
                  onDelete={handleClearChakraFilter}
                  onClick={handleClearChakraFilter}
                  sx={{
                    bgcolor: `${chakraColors[capitalizeChakra(chakraFilter)]}20`,
                    color: chakraColors[capitalizeChakra(chakraFilter)],
                    borderColor: chakraColors[capitalizeChakra(chakraFilter)]
                  }}
                />
              )}
              {emotionFilter && (
                <Chip
                  label={`Emotion: ${emotionFilter}`}
                  onDelete={handleClearEmotionFilter}
                  onClick={handleClearEmotionFilter}
                  color="secondary"
                />
              )}
              {categoryFilter && (
                <Chip
                  label={`Category: ${categoryFilter}`}
                  onDelete={handleClearCategoryFilter}
                  onClick={handleClearCategoryFilter}
                  color="info"
                />
              )}
            </Box>
          )}
        </Box>

        {/* Results */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Typography variant="h6" color="text.secondary">
            {loading ? 'Loading...' : `${filtered.length} video${filtered.length !== 1 ? 's' : ''} found`}
          </Typography>
          {!loading && filtered.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length}
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </Typography>
          )}
        </Box>

        {/* Videos Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No videos found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filters
            </Typography>
          </Box>
        ) : currentPageVideos.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No videos on this page
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try going to an earlier page or changing your page size
            </Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              sx={{ mt: 2 }}
              onClick={() => setCurrentPage(1)}
            >
              Go to First Page
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)'
              },
              gap: 3,
              alignItems: 'stretch'
            }}
          >
            {currentPageVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </Box>
        )}

                 {/* Pagination */}
         {totalPages > 1 && (
           <Box mt={4} display="flex" justifyContent="center">
             <Stack spacing={3} alignItems="center">
               <Pagination
                 count={totalPages}
                 page={currentPage}
                 onChange={handlePageChange}
                 color="primary"
                 size={isMobile ? 'medium' : 'large'}
                 siblingCount={isMobile ? 0 : 1}
                 boundaryCount={isMobile ? 1 : 2}
                 sx={{
                   '& .MuiPaginationItem-root': {
                     borderRadius: 2,
                     fontWeight: 600,
                     fontSize: { xs: '0.875rem', md: '1rem' },
                     color: theme.palette.text.primary,
                     '&.Mui-selected': {
                       bgcolor: theme.palette.primary.main,
                       color: 'white',
                       '&:hover': {
                         bgcolor: theme.palette.primary.dark,
                       },
                     },
                     '&:hover': {
                       bgcolor: theme.palette.action.hover,
                     },
                   },
                 }}
               />
               <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                 <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
                   Items per page:
                 </Typography>
                 <FormControl variant="outlined" size="small" sx={{ minWidth: 80 }}>
                   <Select
                     value={pageSize}
                     onChange={handlePageSizeChange}
                     sx={{
                       '& .MuiSelect-select': {
                         paddingY: 1,
                         paddingX: 2,
                       },
                     }}
                   >
                     <MenuItem value={4}>4</MenuItem>
                     <MenuItem value={16}>16</MenuItem>
                     <MenuItem value={32}>32</MenuItem>
                     <MenuItem value={64}>64</MenuItem>
                   </Select>
                 </FormControl>
               </Box>
             </Stack>
           </Box>
         )}
      </Container>
      
      {/* Watch Modal */}
      <VideoModal
        open={modalOpen}
        onClose={closeWatchModal}
        title={modalVideo?.title || ''}
        videoUrl={modalVideo?.videoUrl || ''}
        content={modalVideo?.fullContent || ''}
        excerpt={modalVideo?.excerpt || ''}
      />
    </Box>
  );
};

export default VideoCatalog; 